import React, { useEffect, useState, useCallback } from "react"
import DataTable, { TableColumn } from "react-data-table-component"
import { Button, Alert, Badge, Spinner } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import useFetch from "../../../_fetch"

interface PublisherUser {
    username: string
    userId: string
    addedAt: string
    active: boolean
}

interface Advertiser {
    username: string
    userId: string
    addedAt: string
}

interface Partnership {
    id: string
    name: string
    type: string
    mode: string
    value: number
    currency: string
    startDate: string
    expiryDate: string | null
    cookieDays: number
    createdAt: string
    domains: string[]
    publisher_users: PublisherUser[]
    advertiser?: Advertiser
    archivedAt?: string | null
    deletedAt?: string | null
}

const PartnershipAgreements: React.FC = () => {
    const { doFetch } = useFetch()
    const navigate = useNavigate()
    const [partnerships, setPartnerships] = useState<Partnership[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchPartnerships = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await doFetch("/user/admin/partnerships?includeDeleted=true")
            if (!res.ok) throw new Error(await res.text())
            const data = await res.json()
            setPartnerships(data)
        } catch (err: any) {
            setError(err?.message || "Failed to load partnerships")
        } finally {
            setLoading(false)
        }
    }, [doFetch])

    useEffect(() => {
        fetchPartnerships()
    }, [fetchPartnerships])

    const formatValue = (row: Partnership) => {
        if (row.type === "sale") return `${row.value}%`
        return `${row.value} ${row.currency}`
    }

    const typeBadgeVariant = (type: string) => {
        if (type === "sale") return "success"
        if (type === "click") return "primary"
        if (type === "lead") return "warning"
        return "secondary"
    }

    const lifecycleLabel = (row: Partnership) => {
        if (row.deletedAt) return "Deleted"
        if (row.archivedAt) return "Archived"
        if (row.expiryDate && new Date(`${row.expiryDate}T23:59:59.999`) < new Date()) return "Expired"
        return "Active"
    }

    const columns: TableColumn<Partnership>[] = [
        {
            name: "ID",
            selector: (row) => row.id,
            sortable: true,
            width: "140px",
            cell: (row) => <code style={{ fontSize: "0.78rem" }}>{row.id}</code>,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            grow: 1.5,
        },
        {
            name: "Type",
            selector: (row) => row.type,
            sortable: true,
            width: "90px",
            cell: (row) => (
                <Badge bg={typeBadgeVariant(row.type)} style={{ fontSize: "0.75rem" }}>
                    {row.type.charAt(0).toUpperCase() + row.type.slice(1)}
                </Badge>
            ),
        },
        {
            name: "Value",
            selector: (row) => row.value,
            sortable: true,
            width: "100px",
            cell: (row) => <span style={{ display: "block", textAlign: "right", width: "100%" }}>{formatValue(row)}</span>,
        },
        {
            name: "Start",
            selector: (row) => row.startDate,
            sortable: true,
            width: "100px",
            cell: (row) => <span style={{ whiteSpace: "nowrap" }}>{row.startDate}</span>,
        },
        {
            name: "Expires",
            selector: (row) => row.expiryDate ?? "",
            sortable: true,
            width: "100px",
            cell: (row) => row.expiryDate
                ? <span style={{ whiteSpace: "nowrap" }}>{row.expiryDate}</span>
                : <span className="text-muted">—</span>,
        },
        {
            name: "Cookie",
            selector: (row) => row.cookieDays,
            sortable: true,
            width: "80px",
            cell: (row) =>
                row.type === "click" ? (
                    <span className="text-muted" style={{ display: "block", textAlign: "right", width: "100%" }}>—</span>
                ) : (
                    <span style={{ display: "block", textAlign: "right", width: "100%" }}>{row.cookieDays}d</span>
                ),
        },
        {
            name: "Publisher",
            grow: 1.5,
            cell: (row) => {
                const publisher = row.publisher_users[0]
                return (
                    <div style={{ lineHeight: 1.4, padding: "4px 0", fontSize: "0.82rem" }}>
                        {!publisher ? (
                            <span className="text-muted">—</span>
                        ) : (
                            <div>
                                <span
                                    style={{
                                        fontSize: "0.72rem",
                                        display: "inline-block",
                                        padding: "1px 6px",
                                        borderRadius: "4px",
                                        background: publisher.active ? "#e9ecef" : "#f8f9fa",
                                        color: publisher.active ? "#343a40" : "#adb5bd",
                                        border: "1px solid #ced4da",
                                    }}
                                >
                                    {publisher.username}
                                </span>
                            </div>
                        )}
                    </div>
                )
            },
        },
        {
            name: "Advertiser",
            grow: 1,
            cell: (row) =>
                row.advertiser ? (
                    <span
                        style={{
                            fontSize: "0.75rem",
                            display: "inline-block",
                            padding: "2px 7px",
                            borderRadius: "4px",
                            background: "#343a40",
                            color: "#fff",
                        }}
                    >
                        {row.advertiser.username}
                    </span>
                ) : (
                    <span className="text-muted" style={{ fontSize: "0.82rem" }}>—</span>
                ),
        },
        {
            name: "Status",
            selector: lifecycleLabel,
            sortable: true,
            width: "100px",
            cell: (row) => <Badge bg={lifecycleLabel(row) === "Active" ? "success" : "secondary"}>{lifecycleLabel(row)}</Badge>,
        },
        {
            name: "Created",
            selector: (row) => row.createdAt,
            sortable: true,
            id: "createdAt",
            width: "110px",
            cell: (row) => <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>{row.createdAt.slice(0, 10)}</span>,
            sortFunction: (first, second) => {
                const firstOpen = first.archivedAt || first.deletedAt ? 0 : 1
                const secondOpen = second.archivedAt || second.deletedAt ? 0 : 1
                if (firstOpen !== secondOpen) return firstOpen - secondOpen
                return first.createdAt.localeCompare(second.createdAt)
            },
        },
        {
            name: "",
            width: "80px",
            cell: (row) => (
                <Button
                    size="sm"
                    variant="outline-primary"
                    style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
                    onClick={() => navigate(`/partnership-agreements/${row.id}`)}
                >
                    View
                </Button>
            ),
        },
    ]

    return (
        <div className="container-fluid px-2 px-md-3 partnership-page-shell">
            <div className="partnership-page-header mb-4">
                <div><h4 className="mb-1">Partnership agreements</h4><div className="text-muted">Manage commercial terms and participant access.</div></div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <div className="card border-0 shadow-sm overflow-hidden"><DataTable
                columns={columns}
                data={[...partnerships].sort((first, second) => Number(!!first.archivedAt || !!first.deletedAt) - Number(!!second.archivedAt || !!second.deletedAt))}
                progressPending={loading}
                progressComponent={<Spinner animation="border" size="sm" className="m-3" />}
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                defaultSortFieldId="createdAt"
                defaultSortAsc={false}
                noDataComponent={<div className="p-3 text-muted">No partnership agreements found.</div>}
                dense
            /></div>


        </div>
    )
}

export default PartnershipAgreements
